<template>
  <div class="wh-screen">
    <template v-if="documentForDialog">
      <warehouse-purchase-order-dialog
        v-model="purchaseOrderDialog"
        :edited-item="documentForDialog"
        :warehouse="documentForDialog.warehouse || {}"
      />
      <warehouse-entry-grocery-dialog
        v-model="purchaseItemDialog"
        :warehouse="documentForDialog.warehouse || {}"
        :edited-item="documentForDialog"
      />
      <warehouse-waybill-dialog
        v-model="waybillDialog"
        :warehouse="documentForDialog.warehouse || {}"
        :edited-item="documentForDialog"
      />
      <warehouse-write-off-dialog
        v-model="writeOffDialog"
        :warehouse="documentForDialog.warehouse || {}"
        :edited-item="documentForDialog"
      />
    </template>
    <warehouse-doc-screen
      :loading="loading"
      :mobile="device.isMobile"
      :columns="columns"
      :rows="rows"
      :grouped="grouped"
      aggregate-available
      :aggregate="aggregateMode"
      :aggregate-columns="aggregateColumns"
      :aggregate-rows="aggregateRows"
      :hint="hint"
      :hint-clickable="Boolean(transactions.length)"
      :actions="[]"
      :empty="empty"
      clickable
      @update:aggregate="aggregateMode = $event"
      @hint="setFiltersToLastTransaction"
      @open-row="openDocumentDialog($event.raw)"
      @toast="$emit('toast', $event)"
    >
      <template #cell="{ row, column }">
        <span v-if="column.key === 'transaction_type' && row.typeLabel" class="wh-status" :class="`wh-status--${row.typeTone}`">
          <v-icon size="14">{{ row.typeIcon }}</v-icon>
          {{ row.typeLabel }}
        </span>
        <template v-else>{{ cellText(row[column.key]) }}</template>
      </template>
      <template #row-actions="{ row }">
        <warehouse-row-button icon="mdi-eye" :label="$t('overview')" tone="pri" action="view" @click="openDocumentDialog(row.raw)" />
      </template>
    </warehouse-doc-screen>
  </div>
</template>

<script>
import WarehouseTabTransaction from "@/components/warehouse/tabs/WarehouseTabTransaction.vue";
import WarehouseDocScreen from "../../docs/WarehouseDocScreen.vue";
import WarehouseRowButton from "../../docs/WarehouseRowButton.vue";
import { TRANSACTION_TYPES } from "../../docs/docStatus";

/**
 * Transakcije magacina (ulaz, izlaz, otpis, narudžbe): lista sa vrstom transakcije u boji, zbirni prikaz po artiklu i
 * pregled dokumenta iz reda. Nasleđuje učitavanje, filtriranje, štampu, Excel i dijaloge iz starog taba.
 */
export default {
  name: "WarehouseScreenTransactions",
  extends: WarehouseTabTransaction,
  components: { WarehouseDocScreen, WarehouseRowButton },
  computed: {
    grouped() {
      return (this.filters.warehouse || []).length > 1;
    },
    columns() {
      return [
        { key: "id", label: "ID", mobile: "sub", muted: true, num: true, prefix: "#" },
        { key: "warehouse_document_id", label: this.$t("warehouse.document_number"), mobile: "meta" },
        { key: "transaction_type", label: this.$t("warehouse.transaction"), mobile: "badge" },
        { key: "code", label: this.$t("code"), muted: true, mobile: "sub" },
        { key: "name", label: this.$t("warehouse.product"), main: true, mobile: "title" },
        { key: "quantity", label: this.$t("quantity"), align: "right", mobile: "meta" },
        { key: "created_at", label: this.$t("date"), mobile: "meta" },
      ];
    },
    rows() {
      return this.filteredTransactions.map((item) => {
        const type = TRANSACTION_TYPES[item.transaction_type];
        const measure = item.grocery?.measure || "";
        return {
          key: item.id,
          raw: item,
          group: { id: item.warehouse_id ?? 0, name: this.toLatin(item.warehouse?.name || "") || this.$t("warehouse.unknown") },
          id: item.id,
          warehouse_document_id: item.warehouse_document_id,
          transaction_type: item.transaction_type,
          typeLabel: type ? this.$t(type.key) : item.transaction_type || "",
          typeTone: type ? type.tone : "neutral",
          typeIcon: type ? type.icon : "mdi-swap-horizontal",
          code: item.grocery?.code || "",
          name: item.grocery?.name || "",
          quantity: item.amount === null || item.amount === undefined ? "" : `${this.formatNumber(item.amount) || item.amount} ${measure}`.trim(),
          created_at: this.formatDateTime(item.created_at),
          sort: {
            id: Number(item.id) || 0,
            warehouse_document_id: Number(item.warehouse_document_id) || 0,
            quantity: Number(item.amount) || 0,
            created_at: item.created_at || "",
          },
        };
      });
    },
    aggregateColumns() {
      return this.transactionAggregateHeaders.map((header) => ({
        key: header.value,
        label: header.text,
        align: header.align === "right" ? "right" : header.align === "center" ? "center" : undefined,
        main: header.value === "grocery_name",
        mobile: header.value === "grocery_name" ? "title" : header.value === "grocery_code" ? "sub" : "meta",
      }));
    },
    aggregateRows() {
      return this.transactionAggregatedItems.map((item, index) => ({
        key: item.grocery_code || `${item.grocery_name}-${index}`,
        grocery_code: item.grocery_code || "",
        grocery_name: this.toLatin(item.grocery_name) || "",
        measure: item.measure || "",
        total_quantity: this.formatNumber(item.total_quantity),
        document_count: item.document_count,
        sort: { total_quantity: Number(item.total_quantity) || 0 },
      }));
    },
    hint() {
      return this.lastTransactionDate ? `${this.$t("warehouse.last_transaction")}: ${this.lastTransactionDate}` : "";
    },
    empty() {
      if (this.aggregateMode) {
        return {
          icon: "mdi-sigma",
          title: this.$t("warehouse.no_aggregate_data"),
          text: this.$t("warehouse.no_entries_for_period"),
        };
      }
      return {
        icon: "mdi-history",
        title: this.$t("no_transactions"),
        text: this.hint || this.$t("no_transactions_yet"),
      };
    },
    shownKpis() {
      if (this.loading) {
        return [];
      }
      return [
        {
          key: "transactions",
          label: this.$t("transactions"),
          value: this.aggregateMode ? this.aggregateRows.length : this.rows.length,
          tone: "inverse",
        },
      ];
    },
  },
  watch: {
    shownKpis: {
      handler(kpis) {
        this.$emit("kpis", kpis);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    cellText(value) {
      return value === undefined || value === null || value === "" ? "—" : value;
    },
  },
};
</script>
